import type { Actions, OrderUnifiedOutput } from "@/api/custom_types";
import { useStatusMap } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useBatchOrderAction } from "@/services";
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useMemo, useState } from "react";

type BulkAction = "cancel" | "pause" | "resume";

interface OrdersBulkActionProps {
	items: OrderUnifiedOutput[];
	onClear: () => void;
}

const OrdersBulkAction = ({ items, onClear }: OrdersBulkActionProps) => {
	const { t } = useTranslation();
	const { orderStatusMap } = useStatusMap();
	const batchOrderAction = useBatchOrderAction();
	const [selectedAction, setSelectedAction] = useState<BulkAction>("pause");
	const [dialogOpen, setDialogOpen] = useState(false);

	const eligibleIds = useMemo<Record<BulkAction, string[]>>(() => {
		const actions: Record<BulkAction, (typeof items)[number][]> = {
			cancel: [],
			pause: [],
			resume: [],
		};
		for (const item of items) {
			const status = orderStatusMap?.[item.status];
			if (status?.can_be_canceled) actions.cancel.push(item);
			if (status?.can_pause) actions.pause.push(item);
			if (status?.can_resume) actions.resume.push(item);
		}
		return Object.fromEntries(
			Object.entries(actions).map(([action, orders]) => [
				action,
				orders.map((order) => order.pk),
			]),
		) as Record<BulkAction, string[]>;
	}, [items, orderStatusMap]);

	const descriptions: Record<BulkAction, string> = {
		cancel: t("order.action.cancelMessage", {
			value: eligibleIds.cancel.length,
		}),
		pause: t("order.action.pauseMessage", { value: eligibleIds.pause.length }),
		resume: t("order.action.playMessage", { value: eligibleIds.resume.length }),
	};

	const labels: Record<BulkAction, string> = {
		cancel: t("action.button.confirmCancel"),
		pause: t("action.button.pause"),
		resume: t("action.button.play"),
	};

	const runAction = () => {
		batchOrderAction.mutate(
			{
				action: selectedAction as Actions,
				orderIds: eligibleIds[selectedAction],
			},
			{
				onSuccess: (skippedIds) => {
					if (skippedIds.length) {
						toast.warning(
							t("order.action.skippedCount", { value: skippedIds.length }),
						);
					}
					setDialogOpen(false);
					onClear();
				},
			},
		);
	};

	const openDialog = (action: BulkAction) => {
		setSelectedAction(action);
		setDialogOpen(true);
	};

	return (
		<>
			<div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-secondary-border bg-background-300 p-1">
				<Button icon variant="text" color="ghost" size="sm" onClick={onClear}>
					<Icon name="close-large-line" size={14} />
				</Button>
				<Typography variant="body-md" className="px-2 py-1.5 font-semibold">
					{t("select.numOfItemsSelected", { num: items.length })}
				</Typography>
				<Button
					color="secondary"
					startIcon={<Icon name="pause-circle-line" />}
					disabled={!eligibleIds.pause.length}
					onClick={() => openDialog("pause")}
				>
					{t("action.pause")}
				</Button>
				<Button
					color="secondary"
					startIcon={<Icon name="play-circle-line" />}
					disabled={!eligibleIds.resume.length}
					onClick={() => openDialog("resume")}
				>
					{t("action.play")}
				</Button>
				<Button
					color="danger"
					startIcon={<Icon name="delete-bin-line" />}
					disabled={!eligibleIds.cancel.length}
					onClick={() => openDialog("cancel")}
				>
					{t("action.cancel")}
				</Button>
			</div>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<div className="mb-6 rounded-md border border-primary-border bg-primary-300 p-4">
							<Typography variant="body-md" className="text-primary-100">
								{t("order.action.eligibleCount", {
									eligible: eligibleIds[selectedAction].length,
									selected: items.length,
								})}
							</Typography>
						</div>
						<Typography variant="body-md">
							{descriptions[selectedAction]}
						</Typography>
					</div>
					<DialogFooter
						buttons={{
							primary: {
								label: labels[selectedAction],
								props: {
									loading: batchOrderAction.isPending,
									onClick: runAction,
								},
							},
							secondary: {
								label: t("common.button.no"),
								props: { onClick: () => setDialogOpen(false) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default OrdersBulkAction;
